import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import {
  BadgeCheck, UserCog, Eye, Loader2, AlertTriangle, Search, Filter,
  Building2, Video, Shield, Check, X, Copy, ScrollText, User, Landmark, Send, CheckCircle2, Clock, Pencil, Download,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   Admin · Gestión de Profesionales (/app/admin/profesionales)
   Lista con RUT/dirección/teléfono/email enmascarados.
   Ficha completa detrás de un modal que loguea el acceso.
   ═══════════════════════════════════════════════════════════════ */

function StatusBadge({ verified, status }) {
  if (verified) return <span className="pill text-[10px] border-emerald-400/40 bg-emerald-400/10 text-emerald-300 inline-flex items-center gap-1"><BadgeCheck size={10}/> Verificado</span>;
  if (status === "rejected") return <span className="pill text-[10px] border-red-400/40 bg-red-400/10 text-red-300">Rechazado</span>;
  return <span className="pill text-[10px] border-amber-400/40 bg-amber-400/10 text-amber-300">Pendiente</span>;
}

export default function AdminGestionProfesionales() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ role: "", verified: "", q: "" });
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [autoEdit, setAutoEdit] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [exporting, setExporting] = useState(false);

  const downloadCSV = async () => {
    setExporting(true);
    setError("");
    try {
      const params = new URLSearchParams();
      // Respetar filtros actuales para el respaldo
      params.set("scope", "filtered");
      if (filters.role) params.set("role", filters.role);
      if (filters.verified !== "") params.set("verified", filters.verified);
      if (filters.q.trim()) params.set("q", filters.q.trim());
      const resp = await api.get(`/admin/users/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `colegalink_profesionales_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = err.message;
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) {
          const j = JSON.parse(txt);
          msg = formatApiError(j.detail) || msg;
        }
      } catch { /* ignore */ }
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filters.role) params.set("role", filters.role);
      if (filters.verified !== "") params.set("verified", filters.verified);
      if (filters.q.trim()) params.set("q", filters.q.trim());
      const { data } = await api.get(`/admin/professionals?${params.toString()}`);
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setItems([]);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (item, opts = {}) => {
    setDetailLoading(true);
    setSelected({ ...item });
    setAutoEdit(!!opts.edit);
    try {
      const { data } = await api.get(`/admin/professionals/${item.id}`);
      setSelected(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSelected(null);
    } finally { setDetailLoading(false); }
  }, []);

  // Deep link ?openUserId=xxx&edit=1 (usado desde Mantenimiento)
  useEffect(() => {
    const uid = searchParams.get("openUserId");
    if (!uid) return;
    const shouldEdit = searchParams.get("edit") === "1";
    openDetail({ id: uid }, { edit: shouldEdit });
    // Limpiar params una vez usados
    const next = new URLSearchParams(searchParams);
    next.delete("openUserId");
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (uid) => {
    try {
      await api.post(`/admin/verifications/${uid}/approve`);
      await load();
      if (selected?.id === uid) setSelected((s) => ({ ...s, verified: true, verification_status: "approved" }));
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const reject = async (uid) => {
    if (!window.confirm("¿Rechazar verificación de este profesional?")) return;
    try {
      await api.post(`/admin/verifications/${uid}/reject`);
      await load();
      if (selected?.id === uid) setSelected((s) => ({ ...s, verified: false, verification_status: "rejected" }));
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const roleLabel = (r) => r === "lawyer" ? "Abogado/a" : r === "mediator" ? "Mediador/a" : r === "requester" ? "Solicitante" : r === "admin" ? "Administrador/a" : r;

  return (
    <div className="container-app py-8" data-testid="admin-gestion-profesionales-page">
      <AdminNav/>
      <div className="flex items-start gap-3">
        <UserCog size={28} className="text-amber-300 mt-1"/>
        <div>
          <h1 className="serif text-3xl">Gestión de Profesionales</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Listado con datos sensibles enmascarados por defecto (Ley 19.628). Cada acceso a la ficha completa queda registrado en el audit log.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="panel p-4 mt-6 flex flex-wrap gap-3 items-end" data-testid="gp-filters">
        <div className="grow min-w-[220px]">
          <label className="text-xs label-eyebrow">Buscar</label>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-3 text-muted-foreground"/>
            <input
              className="field pl-9 mt-1 w-full"
              placeholder="Nombre, email, RUT, N° registro…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="gp-search"
            />
          </div>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Rol</label>
          <select className="field mt-1" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} data-testid="gp-filter-role">
            <option value="">Todos</option>
            <option value="lawyer">Abogado/a</option>
            <option value="mediator">Mediador/a</option>
            <option value="requester">Solicitante</option>
          </select>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Verificado</label>
          <select className="field mt-1" value={filters.verified} onChange={(e) => setFilters({ ...filters, verified: e.target.value })} data-testid="gp-filter-verified">
            <option value="">Todos</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        </div>
        <button className="btn-ghost text-sm" onClick={load} data-testid="gp-refresh"><Filter size={13} className="mr-1"/> Aplicar</button>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-md border border-red-400/40 bg-red-400/10 text-sm text-red-200 inline-flex items-center gap-2" data-testid="gp-error">
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      {/* Toolbar de respaldo (encima de la tabla) */}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {loading ? "Cargando…" : `${items.length} profesional${items.length === 1 ? "" : "es"} listad${items.length === 1 ? "o" : "os"}`}
        </div>
        <button
          type="button"
          onClick={downloadCSV}
          disabled={exporting}
          className="btn-outline text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="gp-download-csv"
          title="Descargar respaldo CSV con los filtros aplicados"
        >
          {exporting ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>}
          📥 Descargar Respaldo CSV
        </button>
      </div>

      {/* Tabla */}
      <div className="mt-4 panel p-0 overflow-x-auto" data-testid="gp-table-wrap">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={16} className="animate-spin"/> Cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="gp-empty">
            <User size={22} className="mx-auto mb-3"/>
            No hay profesionales con estos filtros.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Nombre</th>
                <th className="text-left px-3 py-3">Rol</th>
                <th className="text-left px-3 py-3">RUT (enmascarado)</th>
                <th className="text-left px-3 py-3">Contacto</th>
                <th className="text-left px-3 py-3">Ubicación</th>
                <th className="text-left px-3 py-3">Modalidad</th>
                <th className="text-left px-3 py-3">N° Registro</th>
                <th className="text-left px-3 py-3">Estado</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`gp-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground">{roleLabel(u.role)}</td>
                  <td className="px-3 py-3 font-mono text-xs">{u.rut_masked || "—"}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    <div>{u.email_masked || "—"}</div>
                    <div>{u.phone_masked || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    {u.comuna || "—"}, {u.region || "—"}
                    {u.address_masked && <div className="mt-0.5 font-mono">{u.address_masked}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-0.5">
                      {u.attends_presencial && <span className="text-[10px] text-cyan-200 inline-flex items-center gap-1"><Building2 size={9}/> Presencial</span>}
                      {u.attends_remote && <span className="text-[10px] text-cyan-200 inline-flex items-center gap-1"><Video size={9}/> Telemática</span>}
                      {!u.attends_presencial && !u.attends_remote && <span className="text-[10px] text-muted-foreground">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs">{u.registry_number || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-3"><StatusBadge verified={u.verified} status={u.verification_status}/></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <button
                        className="btn-ghost text-xs py-1.5"
                        onClick={() => openDetail(u)}
                        data-testid={`gp-view-${u.id}`}
                      >
                        <Eye size={11} className="mr-1"/> Ver ficha
                      </button>
                      <button
                        className="btn-ghost text-xs py-1.5 border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
                        onClick={() => openDetail(u, { edit: true })}
                        data-testid={`gp-edit-${u.id}`}
                      >
                        <Pencil size={11} className="mr-1"/> Editar
                      </button>
                      {!u.verified && (
                        <button
                          className="btn-ghost text-xs py-1.5 border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"
                          onClick={() => approve(u.id)}
                          data-testid={`gp-approve-${u.id}`}
                        >
                          <Check size={11} className="mr-1"/> Verificar Perfil
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal detalle — full-screen en móvil, centrado en desktop */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="gp-detail-modal" onClick={() => setSelected(null)}>
          <div className="panel p-4 sm:p-6 max-w-2xl w-full h-[95vh] sm:h-auto sm:max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 flex-wrap sticky top-0 bg-background/95 backdrop-blur -mx-4 sm:-mx-6 px-4 sm:px-6 pb-3 mb-2 border-b border-white/5 z-10">
              <div>
                <div className="inline-flex items-center gap-2 text-xs text-amber-300 mb-2">
                  <Shield size={12}/> Acceso registrado en audit log
                </div>
                <h2 className="serif text-xl sm:text-2xl">{selected.name}</h2>
                <div className="text-xs text-muted-foreground">{roleLabel(selected.role)}</div>
              </div>
              <div className="flex gap-2 items-center">
                <StatusBadge verified={selected.verified} status={selected.verification_status}/>
                <button className="btn-ghost text-xs py-1.5 px-3" onClick={() => setSelected(null)} data-testid="gp-detail-close">✕</button>
              </div>
            </div>

            {detailLoading ? (
              <div className="p-8 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
                <Loader2 size={16} className="animate-spin"/> Cargando ficha completa…
              </div>
            ) : (
              <UserDetailView
                selected={selected}
                setSelected={setSelected}
                approve={approve}
                reject={reject}
                setError={setError}
                autoEdit={autoEdit}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid sm:grid-cols-3 gap-1 sm:gap-3 py-1.5 border-b border-white/5">
      <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`sm:col-span-2 break-words ${mono ? "font-mono text-xs" : "text-sm"}`}>{value || <span className="text-muted-foreground italic">— sin datos —</span>}</div>
    </div>
  );
}

/**
 * Vista completa (lectura + edición) de un usuario.
 * Muestra TODOS los campos del documento y permite editar los definidos en
 * ADMIN_EDITABLE_FIELDS del backend.
 */
function UserDetailView({ selected, setSelected, approve, reject, setError, autoEdit }) {
  const [editing, setEditing] = useState(!!autoEdit);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => { if (autoEdit) setEditing(true); }, [autoEdit]);

  useEffect(() => {
    if (selected) {
      setForm({
        name: selected.name || "",
        email: selected.email || "",
        phone: selected.phone || "",
        rut: selected.rut || "",
        region: selected.region || "",
        comuna: selected.comuna || "",
        domicilio_profesional: selected.domicilio_profesional || "",
        registry_number: selected.registry_number || "",
        bio: selected.bio || "",
        office_address: selected.office_address || "",
        mediator_registry_number: selected.mediator_registry_number || "",
        specialties: (selected.specialties || []).join(", "),
        cortes_apelaciones: (selected.cortes_apelaciones || []).join(", "),
        comunas_atendidas: (selected.comunas_atendidas || []).join(", "),
        attends_presencial: !!selected.attends_presencial,
        attends_remote: !!selected.attends_remote,
        account_status: selected.account_status || "active",
        plan: selected.plan || "",
      });
    }
  }, [selected, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg({ type: "", text: "" });
    try {
      const updates = { ...form };
      // Convertir strings CSV a arrays
      updates.specialties = updates.specialties ? updates.specialties.split(",").map(s => s.trim()).filter(Boolean) : [];
      updates.cortes_apelaciones = updates.cortes_apelaciones ? updates.cortes_apelaciones.split(",").map(s => s.trim()).filter(Boolean) : [];
      updates.comunas_atendidas = updates.comunas_atendidas ? updates.comunas_atendidas.split(",").map(s => s.trim()).filter(Boolean) : [];
      // No mandar campos vacíos si no cambian
      const { data } = await api.patch(`/admin/professionals/${selected.id}`, { updates });
      setSelected(data.user);
      setMsg({ type: "success", text: "Cambios guardados y auditados." });
      setEditing(false);
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
      setError?.(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!selected) return null;

  return (
    <div className="mt-5 space-y-4 text-sm">
      {/* Toolbar Edit / Save */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
          <Shield size={11}/> Los cambios quedan registrados en el audit log
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="btn-outline text-xs inline-flex items-center gap-1.5"
            data-testid="gp-edit-toggle"
          >
            <Pencil size={12}/> Editar campos
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => { setEditing(false); setMsg({ type: "", text: "" }); }}
              className="btn-ghost text-xs"
              data-testid="gp-edit-cancel"
            >Cancelar</button>
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="gp-edit-save"
            >
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Guardar
            </button>
          </div>
        )}
      </div>

      {msg.text && (
        <div className={`text-xs rounded-md px-3 py-2 ${msg.type === "success" ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border border-red-500/40 bg-red-500/10 text-red-200"}`} data-testid="gp-edit-msg">
          {msg.text}
        </div>
      )}

      {/* Datos personales */}
      <SectionTitle>Datos personales</SectionTitle>
      <EditableRow label="Nombre" field="name" editing={editing} form={form} set={set} value={selected.name}/>
      <EditableRow label="RUT" field="rut" editing={editing} form={form} set={set} value={selected.rut} mono/>
      <EditableRow label="Email" field="email" editing={editing} form={form} set={set} value={selected.email}/>
      <EditableRow label="Teléfono" field="phone" editing={editing} form={form} set={set} value={selected.phone}/>
      <EditableRow label="Región" field="region" editing={editing} form={form} set={set} value={selected.region}/>
      <EditableRow label="Comuna" field="comuna" editing={editing} form={form} set={set} value={selected.comuna}/>
      {/* Domicilio: etiqueta y visibilidad dependen del rol.
          - Solicitante  → "Domicilio Particular" (dato personal)
          - Abogado/Med  → "Domicilio profesional" (para Minuta Delega Poder / oficina)
          - Admin        → oculto (no aplica) */}
      {selected.role === "requester" && (
        <EditableRow label="Domicilio Particular" field="domicilio_profesional" editing={editing} form={form} set={set} value={selected.domicilio_profesional}/>
      )}
      {(selected.role === "lawyer" || selected.role === "mediator") && (
        <EditableRow label="Domicilio profesional" field="domicilio_profesional" editing={editing} form={form} set={set} value={selected.domicilio_profesional}/>
      )}
      {/* Bio: sólo para profesionales (abogado / mediador). El solicitante no tiene bio. */}
      {(selected.role === "lawyer" || selected.role === "mediator") && (
        <EditableRow label="Bio" field="bio" editing={editing} form={form} set={set} value={selected.bio} multiline/>
      )}

      {/* Datos profesionales (lawyer / mediator) */}
      {(selected.role === "lawyer" || selected.role === "mediator") && (
        <>
          <SectionTitle>Datos profesionales</SectionTitle>
          <EditableRow label="N° Registro Min. Justicia" field="registry_number" editing={editing} form={form} set={set} value={selected.registry_number} mono/>
          <EditableRow label="Estado de cuenta" field="account_status" editing={editing} form={form} set={set} value={selected.account_status || "active"}
            options={[
              { value: "active", label: "Activa" },
              { value: "suspended", label: "Suspendida" },
              { value: "banned", label: "Baneada" },
            ]}
          />
          <EditableRow label="Plan" field="plan" editing={editing} form={form} set={set} value={selected.plan || "—"}/>
        </>
      )}

      {/* Específico Mediador */}
      {selected.role === "mediator" && (
        <>
          <SectionTitle>Mediación familiar privada</SectionTitle>
          <EditableRow label="N° Inscripción MedFamPriv" field="mediator_registry_number" editing={editing} form={form} set={set} value={selected.mediator_registry_number} mono/>
          <EditableRow label="Cortes de Apelaciones (coma-sep)" field="cortes_apelaciones" editing={editing} form={form} set={set} value={(selected.cortes_apelaciones || []).join(", ") || "—"}/>
          <EditableRow label="Comunas atendidas (coma-sep)" field="comunas_atendidas" editing={editing} form={form} set={set} value={(selected.comunas_atendidas || []).join(", ") || "—"}/>
          <EditableRow label="Atiende presencial" field="attends_presencial" editing={editing} form={form} set={set} value={selected.attends_presencial ? "Sí" : "No"} boolean/>
          <EditableRow label="Atiende telemática" field="attends_remote" editing={editing} form={form} set={set} value={selected.attends_remote ? "Sí" : "No"} boolean/>
          <EditableRow label="Dirección de oficina" field="office_address" editing={editing} form={form} set={set} value={selected.office_address}/>
          <Row label="Tarifario" value={
            selected.mediator_tariff
              ? `Base $${selected.mediator_tariff.valor_base_frustrada_clp?.toLocaleString("es-CL") || 0} · Total con acuerdo $${selected.mediator_tariff.valor_acuerdo_total_clp?.toLocaleString("es-CL") || 0}`
              : "No configurado"
          }/>
          <Row label="Reputación" value={`${(selected.reputation ?? "—")} ⭐ (${selected.reviews_count || 0} reseñas)`}/>
          <Row label="Bloques disponibilidad" value={`${(selected.weekly_pattern || []).length} bloques configurados`}/>
        </>
      )}

      {/* Específico Abogado */}
      {selected.role === "lawyer" && (
        <>
          <SectionTitle>Delegaciones (Abogado)</SectionTitle>
          <EditableRow label="Especialidades (coma-sep)" field="specialties" editing={editing} form={form} set={set} value={(selected.specialties || []).join(", ") || "—"}/>
          <Row label="Certificado título" value={selected.has_certificate ? "✓ Cargado" : "✗ No cargado"}/>
          <Row label="Delegaciones gratis usadas" value={`${selected.free_delegations_used || 0} de ${selected.free_delegations_quota || 0}`}/>
        </>
      )}

      {/* Específico Solicitante */}
      {selected.role === "requester" && (
        <>
          <SectionTitle>Solicitante</SectionTitle>
          <Row label="Total mediaciones solicitadas" value={selected.total_mediations_requested ?? "—"}/>
        </>
      )}

      {/* Sistema / cuenta */}
      <SectionTitle>Sistema</SectionTitle>
      <Row label="ID" value={selected.id} mono/>
      <Row label="Rol" value={selected.role}/>
      <Row label="Verificado" value={selected.verified ? "Sí" : "No"}/>
      <Row label="Estado de verificación" value={selected.verification_status || "—"}/>
      <Row label="Cuenta creada" value={selected.created_at ? new Date(selected.created_at).toLocaleString("es-CL") : "—"}/>
      <Row label="Última actualización" value={selected.updated_at ? new Date(selected.updated_at).toLocaleString("es-CL") : "—"}/>
      <Row label="Login previo" value={selected.last_login_at ? new Date(selected.last_login_at).toLocaleString("es-CL") : "—"}/>
      <Row label="Foto de perfil" value={selected.has_avatar ? "✓" : "—"}/>
      <Row label="Suspensión mediador (si aplica)" value={selected.mediator_suspension_status || "—"}/>

      {/* Datos bancarios + Payouts — solo aplican a profesionales */}
      {(selected.role === "lawyer" || selected.role === "mediator") && (
        <>
          <BankingInfoBlock user={selected}/>
          <PayoutBlock user={selected}/>
        </>
      )}

      {/* Botones de verificación */}
      {(selected.role === "lawyer" || selected.role === "mediator") && (
        <div className="flex gap-2 pt-4 border-t border-white/10">
          {!selected.verified && (
            <button
              className="btn-primary text-sm inline-flex items-center gap-2"
              onClick={() => approve(selected.id)}
              data-testid="gp-detail-approve"
            >
              <Check size={14}/> Verificar Perfil
            </button>
          )}
          {selected.verified && (
            <button
              className="btn-ghost text-sm text-red-300 hover:text-red-200 inline-flex items-center gap-2"
              onClick={() => reject(selected.id)}
              data-testid="gp-detail-reject"
            >
              <X size={14}/> Revocar verificación
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mt-6 mb-2 text-[11px] uppercase tracking-widest text-amber-300 font-semibold border-b border-amber-300/20 pb-1">
      {children}
    </div>
  );
}

function EditableRow({ label, field, editing, form, set, value, mono, multiline, boolean: isBoolean, options }) {
  if (!editing) {
    return (
      <Row label={label} value={typeof value === "string" ? value : (value ?? "")} mono={mono}/>
    );
  }
  const v = form[field];
  return (
    <div className="grid sm:grid-cols-3 gap-1 sm:gap-3 py-1.5 border-b border-white/5 items-start">
      <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-amber-300 font-medium pt-2">{label}</div>
      <div className="sm:col-span-2">
        {isBoolean ? (
          <label className="inline-flex items-center gap-2 py-2">
            <input type="checkbox" checked={!!v} onChange={(e) => set(field, e.target.checked)} data-testid={`gp-edit-${field}`}/>
            <span className="text-xs">{v ? "Sí" : "No"}</span>
          </label>
        ) : options ? (
          <select className="field w-full" value={v || ""} onChange={(e) => set(field, e.target.value)} data-testid={`gp-edit-${field}`}>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : multiline ? (
          <textarea rows={3} className={`field w-full ${mono ? "font-mono text-xs" : ""}`} value={v || ""} onChange={(e) => set(field, e.target.value)} data-testid={`gp-edit-${field}`}/>
        ) : (
          <input type="text" className={`field w-full ${mono ? "font-mono text-xs" : ""}`} value={v || ""} onChange={(e) => set(field, e.target.value)} data-testid={`gp-edit-${field}`}/>
        )}
      </div>
    </div>
  );
}

const ACCOUNT_TYPE_LABEL = {
  corriente: "Cuenta Corriente",
  vista: "Cuenta Vista / RUT",
  ahorro: "Cuenta de Ahorro",
};

function BankingInfoBlock({ user }) {
  const bi = user?.banking_info;
  const complete = !!user?.banking_info_complete;
  return (
    <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="gp-banking-block">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="inline-flex items-center gap-2 text-emerald-300">
          <Landmark size={15}/>
          <span className="text-xs uppercase tracking-widest font-semibold">Datos bancarios (Pago Protegido)</span>
        </div>
        {complete ? (
          <span className="pill text-[10px] border-emerald-400/50 bg-emerald-400/10 text-emerald-300 inline-flex items-center gap-1">
            <Check size={10}/> Listo para pagos
          </span>
        ) : (
          <span className="pill text-[10px] border-amber-400/50 bg-amber-400/10 text-amber-300 inline-flex items-center gap-1">
            <AlertTriangle size={10}/> Incompleto
          </span>
        )}
      </div>
      {!bi ? (
        <div className="text-xs text-muted-foreground italic">El profesional aún no ha registrado sus datos bancarios.</div>
      ) : (
        <div className="space-y-1 text-sm">
          <Row label="Titular" value={bi.account_holder_name}/>
          <Row label="RUT titular" value={bi.account_holder_rut} mono/>
          <Row label="Banco" value={bi.bank}/>
          <Row label="Tipo de cuenta" value={ACCOUNT_TYPE_LABEL[bi.account_type] || bi.account_type}/>
          <Row label="Nº de cuenta" value={bi.account_number} mono/>
          <Row label="Email de aviso" value={bi.notification_email}/>
          <Row label="Actualizado" value={bi.updated_at ? new Date(bi.updated_at).toLocaleString("es-CL") : "—"}/>
        </div>
      )}
    </div>
  );
}

function PayoutBlock({ user }) {
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [refType, setRefType] = useState("manual");
  const [refId, setRefId] = useState("");
  const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptErr, setReceiptErr] = useState("");

  const canAuthorize = !!user?.banking_info_complete;

  const loadHistory = useCallback(async () => {
    if (!user?.id) return;
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/admin/professionals/${user.id}/payouts`);
      setHistory(data.items || []);
    } catch (err) {
      // silencioso
    } finally { setLoadingHistory(false); }
  }, [user?.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    if (!amount || parseInt(amount, 10) <= 0) {
      return setMsg({ type: "error", text: "Ingresa un monto válido (mayor a 0)." });
    }
    if (!receiptFile) {
      return setMsg({ type: "error", text: "Adjunta el comprobante bancario antes de confirmar la transferencia." });
    }
    setBusy(true);
    try {
      const file_b64 = await new Promise((ok, ko) => {
        const r = new FileReader();
        r.onload = () => ok(String(r.result || "").split(",").pop());
        r.onerror = ko;
        r.readAsDataURL(receiptFile);
      });
      const { data } = await api.post(`/admin/professionals/${user.id}/payouts`, {
        professional_id: user.id,
        amount_clp: parseInt(amount, 10),
        reference_type: refType,
        reference_id: refId.trim() || null,
        notes: notes.trim() || null,
        transfer_date: transferDate || null,
        receipt_b64: file_b64,
        receipt_name: receiptFile.name,
        receipt_mime: receiptFile.type || "application/octet-stream",
      });
      const emailOk = data.email_sent;
      setMsg({
        type: "success",
        text: `Transferencia registrada con comprobante. ${emailOk ? "Email de aviso enviado al profesional ✓" : "⚠ No se pudo enviar el email (revisa configuración de Resend)."}`,
      });
      setAmount(""); setNotes(""); setRefId(""); setReceiptFile(null); setReceiptErr("");
      setShowForm(false);
      loadHistory();
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally { setBusy(false); }
  };

  const onReceiptChange = (e) => {
    setReceiptErr("");
    const f = e.target.files?.[0];
    if (!f) { setReceiptFile(null); return; }
    if (f.size > 5 * 1024 * 1024) { setReceiptErr("El archivo supera los 5 MB."); return; }
    const ok = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!ok) { setReceiptErr("Solo se aceptan imágenes (JPG/PNG) o PDF."); return; }
    setReceiptFile(f);
  };

  const CLP = (n) => "$" + (n || 0).toLocaleString("es-CL");

  return (
    <div className="mt-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4" data-testid="gp-payout-block">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-cyan-300">
          <Send size={15}/>
          <span className="text-xs uppercase tracking-widest font-semibold">Autorizar transferencia</span>
        </div>
        {canAuthorize ? (
          <button
            className="btn-primary text-xs inline-flex items-center gap-1.5"
            onClick={() => setShowForm((v) => !v)}
            data-testid="gp-payout-toggle"
          >
            <Send size={12}/> {showForm ? "Cerrar formulario" : "Autorizar nueva transferencia"}
          </button>
        ) : (
          <span className="text-[11px] text-amber-300 inline-flex items-center gap-1">
            <AlertTriangle size={11}/> Requiere datos bancarios completos
          </span>
        )}
      </div>

      {showForm && canAuthorize && (
        <form onSubmit={submit} className="mt-3 grid sm:grid-cols-2 gap-3 p-3 rounded-lg bg-black/20 border border-white/10">
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Monto (CLP) *</span>
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="field mt-1 w-full"
              placeholder="Ej: 40000"
              data-testid="gp-payout-amount"
              required
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Tipo de referencia</span>
            <select value={refType} onChange={(e) => setRefType(e.target.value)} className="field mt-1 w-full" data-testid="gp-payout-reftype">
              <option value="manual">Manual</option>
              <option value="mediation">Mediación</option>
              <option value="delegation">Delegación</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">ID de referencia (opcional)</span>
            <input type="text" value={refId} onChange={(e) => setRefId(e.target.value)} className="field mt-1 w-full" placeholder="ID mediación/delegación" data-testid="gp-payout-refid"/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Fecha de transferencia</span>
            <input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} className="field mt-1 w-full" data-testid="gp-payout-date"/>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Notas (opcional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="field mt-1 w-full"
              placeholder="Concepto, observaciones…"
              data-testid="gp-payout-notes"
            />
          </label>
          <div className="sm:col-span-2 p-3 rounded-md border border-dashed border-cyan-400/40 bg-cyan-400/[0.03]">
            <div className="text-[10px] uppercase tracking-wider text-cyan-300 inline-flex items-center gap-1">
              📎 Comprobante bancario (obligatorio) *
            </div>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={onReceiptChange}
              className="mt-2 block w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-white/10 file:text-foreground hover:file:bg-white/15 cursor-pointer"
              data-testid="gp-payout-receipt"
            />
            {receiptFile && !receiptErr && (
              <div className="mt-2 text-[11px] text-emerald-300 inline-flex items-center gap-1" data-testid="gp-payout-receipt-ok">
                <Check size={11}/> {receiptFile.name} listo
              </div>
            )}
            {receiptErr && <div className="mt-2 text-xs text-red-300" data-testid="gp-payout-receipt-err">{receiptErr}</div>}
            <div className="mt-1 text-[10px] text-muted-foreground italic">
              El botón &quot;Confirmar transferencia&quot; se habilita cuando adjuntas la imagen o PDF del respaldo bancario.
            </div>
          </div>
          {msg.text && (
            <div className={`sm:col-span-2 text-xs rounded-md px-3 py-2 ${
              msg.type === "success" ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border border-red-500/40 bg-red-500/10 text-red-200"
            }`} data-testid="gp-payout-msg">{msg.text}</div>
          )}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={busy || !receiptFile}
              className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="gp-payout-submit"
            >
              {busy ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}
              {busy ? "Autorizando…" : "Confirmar transferencia y enviar email al profesional"}
            </button>
          </div>
        </form>
      )}

      {/* Historial */}
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1">
          <Clock size={11}/> Historial de transferencias ({history.length})
        </div>
        {loadingHistory ? (
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin"/> Cargando…</div>
        ) : history.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">Sin transferencias autorizadas aún.</div>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {history.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-xs p-2 rounded bg-black/20 border border-white/5" data-testid={`gp-payout-item-${p.id}`}>
                <div>
                  <div className="font-medium text-emerald-300">{CLP(p.amount_clp)}</div>
                  <div className="text-[10px] text-muted-foreground">{p.transfer_date} · {p.reference_type}{p.reference_id ? ` #${p.reference_id.slice(0,8)}` : ""}</div>
                  {p.notes && <div className="text-[10px] text-muted-foreground italic mt-0.5">{p.notes}</div>}
                </div>
                <span className={`pill text-[9px] ${p.email_sent ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-amber-400/40 bg-amber-400/10 text-amber-300"}`}>
                  {p.email_sent ? "Email ✓" : "Email ✗"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
